type UTMParams = Record<string, any>

const ATTRIBUTION_STORAGE_KEY = 'checkout_attribution_snapshot_v1'

const TRUE_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_adgroup',
  'utm_network',
  'utm_placement',
  'gclid',
  'fbclid',
]

const ATTRIBUTION_KEY_ALIASES: Record<string, string> = {
  gcl_id: 'gclid',
  fbcl_id: 'fbclid',
}

function pickTruthy(obj: UTMParams) {
  return Object.entries(obj || {}).reduce((acc, [previous, current]) => {
    if (current !== undefined && current !== null && `${current}`.trim() !== '')
      acc[previous] = current
    return acc
  }, {} as UTMParams)
}

function isBrowserEnvironment() {
  return typeof window !== 'undefined'
}

function readClickIdFromSession(storage: Storage | undefined, ...keys: string[]) {
  if (!storage) return ''
  const foundKey = keys.find(key => {
    const value = storage.getItem(key)
    return Boolean(value && `${value}`.trim())
  })
  if (!foundKey) return ''
  return `${storage.getItem(foundKey)}`.trim()
}

function persistClickIdsToSession(attribution: Record<string, string>) {
  if (!isBrowserEnvironment()) return
  try {
    const ss = window.sessionStorage || undefined
    if (!ss) return

    if (attribution.gclid) {
      ss.setItem('gclid', attribution.gclid)
      ss.setItem('gcl_id', attribution.gclid)
    }
    if (attribution.fbclid) {
      ss.setItem('fbclid', attribution.fbclid)
      ss.setItem('fbcl_id', attribution.fbclid)
    }
  } catch {
    // Intentionally ignoring sessionStorage errors
  }
}

export function normalizeAttributionFields(input: UTMParams = {}) {
  const source = input || {}
  const normalized = {} as Record<string, string>

  TRUE_KEYS.forEach(key => {
    const value = source[key]
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      normalized[key] = `${value}`.trim()
    }
  })

  Object.entries(ATTRIBUTION_KEY_ALIASES).forEach(([aliasKey, canonicalKey]) => {
    if (normalized[canonicalKey]) return
    const value = source[aliasKey]
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      normalized[canonicalKey] = `${value}`.trim()
    }
  })

  return normalized
}

export function readAttributionSnapshot() {
  if (!isBrowserEnvironment()) return {}
  try {
    const raw = window.localStorage?.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return normalizeAttributionFields(parsed || {})
  } catch {
    return {}
  }
}

export function persistAttributionSnapshot(snapshot: UTMParams = {}) {
  if (!isBrowserEnvironment()) return
  const normalized = normalizeAttributionFields(snapshot)
  try {
    if (!Object.keys(normalized).length) {
      window.localStorage?.removeItem(ATTRIBUTION_STORAGE_KEY)
      return
    }
    window.localStorage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(normalized))
  } catch {
    // Intentionally ignoring localStorage errors
  }
  persistClickIdsToSession(normalized)
}

export function mergeAndPersistAttribution(currentAttribution: UTMParams = {}) {
  const stored = readAttributionSnapshot()
  const normalizedCurrent = normalizeAttributionFields(currentAttribution)
  const merged = { ...stored, ...normalizedCurrent }
  persistAttributionSnapshot(merged)
  return merged
}

export function getAttributionFromSearchParams(params: URLSearchParams) {
  const extracted = {} as UTMParams
  TRUE_KEYS.forEach(key => {
    const value = params.get(key)
    if (value !== null) extracted[key] = value
  })
  Object.keys(ATTRIBUTION_KEY_ALIASES).forEach(aliasKey => {
    const value = params.get(aliasKey)
    if (value !== null) extracted[aliasKey] = value
  })
  return normalizeAttributionFields(extracted)
}

export function getAttributionFields(storeUtmParams: UTMParams = {}) {
  const storeNormalized = normalizeAttributionFields(storeUtmParams)
  const storedSnapshot = readAttributionSnapshot()
  const base = { ...storedSnapshot, ...storeNormalized }

  try {
    const ss = window.sessionStorage || undefined
    if (ss) {
      const g = readClickIdFromSession(ss, 'gclid', 'gcl_id')
      const f = readClickIdFromSession(ss, 'fbclid', 'fbcl_id')
      if (g && !base.gclid) base.gclid = g
      if (f && !base.fbclid) base.fbclid = f
    }
  } catch {
    // Intentionally ignoring sessionStorage errors
  }

  const filtered = pickTruthy(normalizeAttributionFields(base))
  if (Object.keys(filtered).length) {
    persistAttributionSnapshot(filtered)
  }

  return filtered
}
